/**
 * Preferences — language, theme, pacing, and runtime knobs.
 *
 * Persisted in the Conversation_Store and applied to subsequent System_Prompt
 * builds without a page reload (Requirements 13.1, 13.2).
 */

import type { ModelId } from '../modelRouter/types';

/** Supported UI / conversation languages. */
export type LanguageCode = 'tr' | 'en' | 'el';

/** Language-practice mode (Requirement 3.4). `'off'` disables practice. */
export type PracticeMode = 'off' | 'en' | 'el';

/** Compliment_Pacing_Policy intensity (Requirement 13.1). */
export type PacingIntensity = 'subtle' | 'balanced' | 'warm';

/** Reduced-motion override (Requirement 13.1). `'system'` follows OS pref. */
export type ReducedMotionOverride = 'system' | 'on' | 'off';

/**
 * Gemini Live API prebuilt voices for the voice-call feature.
 *
 * Curated to feminine / soft voices that the native-audio model
 * actually accepts. Some of the 30 published voices are gated to
 * other Live API variants and silently reject the session when used
 * with `gemini-2.5-flash-native-audio-preview-12-2025`, leaving the
 * UI stuck in "Listening" forever — so we only ship the ones
 * confirmed to work end-to-end. Default is `'Aoede'` (warm, breezy,
 * soft feminine).
 */
export type VoiceName =
  | 'Aoede'
  | 'Leda'
  | 'Kore'
  | 'Zephyr';

export interface Preferences {
  schemaVersion: 1;
  /** Default `'tr'` (Requirement 3.1). */
  language: LanguageCode;
  /** Default `'off'` (Requirement 3.4). */
  practiceMode: PracticeMode;
  /** Default `'barbie'` (Requirement 8.1, 8.7). */
  activeThemeId: string;
  /** Default `'balanced'` (Requirement 13.1). */
  pacing: PacingIntensity;
  /** Default `'system'` (Requirement 7.5, 13.1). */
  reducedMotionOverride: ReducedMotionOverride;
  /** Default 8000 tokens (Requirement 4.4). */
  tokenBudget: number;
  /** Dev-only override (Requirement 13.4). Default `null`. */
  forcedModel: ModelId | null;
  /** Live API voice for sesli konuşma. Default `'Aoede'`. */
  voiceName: VoiceName;
}

/**
 * Returns the seeded default Preferences.
 *
 * Returns a fresh object on each call so callers can mutate the result
 * without leaking back into shared module state.
 */
export function defaultPreferences(): Preferences {
  return {
    schemaVersion: 1,
    language: 'tr',
    practiceMode: 'off',
    activeThemeId: 'barbie',
    pacing: 'balanced',
    reducedMotionOverride: 'system',
    tokenBudget: 8000,
    forcedModel: null,
    voiceName: 'Aoede',
  };
}
